import { Icon } from '@zoom/components';
import classnames from 'classnames';
import React, { useState } from 'react';
import { FieldRenderProps } from 'react-final-form';
import { Input, InputProps, Label } from 'reactstrap';

import { useAuth } from '../../app/AuthorizationProvider';

export type RenderInputProps = InputProps &
  FieldRenderProps<number> & {
    dataTestId?: string;
    disabled?: boolean;
    isPercentage?: boolean;
    noValidation?: boolean;
  };

export const finalFormNumberInputHelper =
  (
    inputProps: InputProps & {
      dataTestId?: string;
      isPercentage?: boolean;
      noValidation?: boolean;
      label?: string;
      toCentre?: boolean;
    }
  ) =>
  (fieldRenderProps: FieldRenderProps<number>) =>
    (
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      <RenderNumberInput {...inputProps} {...fieldRenderProps} />
    );

export const RenderNumberInput: React.FC<RenderInputProps> = ({
  dataTestId,
  disabled = false,
  input: { name, onChange, value, ...inputProps },
  meta,
  label,
  noValidation = true,
  isPercentage = false,
  toCentre = true,
  ...rest
}) => {
  const { locale } = useAuth();

  const [isFocused, setIsFocused] = useState(false);

  const min = rest.min ?? (isPercentage ? 0 : -Number.MAX_VALUE);
  const max = rest.max ?? (isPercentage ? 100 : Number.MAX_VALUE);

  const className = classnames('number-input-wrapper', 'text-input', {
    'text-input--valid':
      (!noValidation && !meta.pristine && meta.valid) || (!noValidation && value && !meta.dirty && !disabled),
    'text-input--invalid': meta.dirty && meta.error,
    'text-input--disabled': disabled,
    'text-input--percentage': isPercentage,
    'number-input-wrapper--centred': toCentre,
  });

  const percentageLocalizedString = Number(1).toLocaleString(locale, { style: 'percent' });
  const percentageLocaleOffset = /\s/g.test(percentageLocalizedString) ? 1 : 0;
  // TODO: ar-EG locale uses different sign for percentage (٪) so it needs to be considered when adding support for that language
  const percentageReversedOrder = percentageLocalizedString.indexOf('%') === 0;

  const valueDigitCount: number = value ? Math.min(value.toString().length, 4) : 0;

  const increment = () => {
    if (disabled) {
      return;
    }

    if (typeof value !== 'number') {
      onChange(Number(min));
    }

    if (typeof value === 'number' && value < Number(max)) {
      onChange(value + 1);
    }
  };

  const decrement = () => {
    if (disabled) {
      return;
    }

    if (typeof value !== 'number') {
      onChange(Number(max));
    }

    if (typeof value === 'number' && value > Number(min)) {
      onChange(value - 1);
    }
  };

  const onChangeValidated = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.value === '') {
      onChange(undefined);
      return;
    }

    const parsedValue = Number.parseInt(e.target.value, 10);

    // eslint-disable-next-line no-restricted-globals
    if (isNaN(parsedValue) || parsedValue < Number(min) || parsedValue > Number(max)) {
      onChange(value);
      return;
    }

    if (parsedValue === 0 && e.target.value.length > 1) {
      // prevent writing multiple 0 in a row
      e.target.value = parsedValue.toString();
    }

    onChange(parsedValue);
  };

  const checkInput = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (/[^\d]/gi.test(String.fromCharCode(e.charCode || e.which))) {
      e.preventDefault();
    }
  };

  const handleFocus = () => {
    setIsFocused(true);
  };

  const handleBlur = () => {
    setIsFocused(false);
  };

  return (
    <div className={className}>
      {label && (
        <Label
          for={name}
          className={classnames('TextInputFloating', 'Label', (typeof value === 'number' || isFocused) && 'active')}
        >
          {label}
        </Label>
      )}
      <Input
        className={classnames({
          'TextInputFloating Input': label,
          'number-input--reversed-order': isPercentage && percentageReversedOrder,
        })}
        data-test-id={dataTestId}
        label={label}
        disabled={disabled}
        {...inputProps}
        id={name}
        name={name}
        onChange={onChangeValidated}
        onKeyPress={checkInput}
        value={value && Number(value).toString()}
        {...rest}
        autoComplete="off"
        type="number"
        onFocus={handleFocus}
        onBlur={handleBlur}
      />
      {isPercentage && (
        <span
          className={classnames('percentage', `percentage--offset-${valueDigitCount}`, {
            'percentage--extra-offset': percentageLocaleOffset,
            'percentage--reversed-order': percentageReversedOrder,
          })}
        >
          %
        </span>
      )}
      <div
        onClick={increment}
        data-test-id={dataTestId && `${dataTestId}-button-increment`}
        className={classnames('number-button number-button--up', {
          'number-button--disabled': disabled || value === max,
        })}
      >
        <Icon kind="chevron-up" color="grey2" size={10} />
      </div>
      <div
        onClick={decrement}
        data-test-id={dataTestId && `${dataTestId}-button-decrement`}
        className={classnames('number-button number-button--down', {
          'number-button--disabled': disabled || value === min,
        })}
      >
        <Icon kind="chevron-down" color="grey2" size={10} />
      </div>
    </div>
  );
};
